import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock, MapPin, Users, Bell, Link } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EVENT_TYPES = ['meeting', 'task', 'reminder', 'deadline'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const eventTypeColors = {
  meeting: 'bg-blue-500',
  task: 'bg-amber-500',
  reminder: 'bg-purple-500',
  deadline: 'bg-rose-500'
};

// Event Form Component
const EventForm = ({ event, onSave, onClose }) => {
  const [form, setForm] = useState({
    title: event?.title || '',
    description: event?.description || '',
    start_datetime: event?.start_datetime || new Date().toISOString().slice(0, 16),
    end_datetime: event?.end_datetime || new Date(Date.now() + 3600000).toISOString().slice(0, 16),
    all_day: event?.all_day || false,
    location: event?.location || '',
    attendees: event?.attendees || [],
    reminder_minutes: event?.reminder_minutes || 15,
    event_type: event?.event_type || 'meeting',
    recurrence: event?.recurrence || null
  });
  const [attendeeInput, setAttendeeInput] = useState('');
  const [saving, setSaving] = useState(false);

  const addAttendee = () => {
    if (attendeeInput && !form.attendees.includes(attendeeInput)) {
      setForm({ ...form, attendees: [...form.attendees, attendeeInput] });
      setAttendeeInput('');
    }
  };

  const removeAttendee = (email) => {
    setForm({ ...form, attendees: form.attendees.filter(a => a !== email) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (event?.id) {
        await axios.put(`${API}/calendar/events/${event.id}`, form);
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/calendar/events`, form);
        toast.success('Event created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Event Title *</Label>
        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Meeting with client" data-testid="event-title-input" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Event Type</Label>
          <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {EVENT_TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Reminder</Label>
          <Select value={String(form.reminder_minutes)} onValueChange={(v) => setForm({ ...form, reminder_minutes: parseInt(v) })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">No reminder</SelectItem>
              <SelectItem value="5">5 minutes before</SelectItem>
              <SelectItem value="15">15 minutes before</SelectItem>
              <SelectItem value="30">30 minutes before</SelectItem>
              <SelectItem value="60">1 hour before</SelectItem>
              <SelectItem value="1440">1 day before</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Start *</Label>
          <Input type="datetime-local" value={form.start_datetime} onChange={(e) => setForm({ ...form, start_datetime: e.target.value })} required />
        </div>
        <div className="space-y-2">
          <Label>End *</Label>
          <Input type="datetime-local" value={form.end_datetime} onChange={(e) => setForm({ ...form, end_datetime: e.target.value })} required />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Location</Label>
        <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Office or video link" />
      </div>

      <div className="space-y-2">
        <Label>Attendees</Label>
        <div className="flex gap-2">
          <Input value={attendeeInput} onChange={(e) => setAttendeeInput(e.target.value)} placeholder="email@example.com" onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAttendee())} />
          <Button type="button" variant="outline" onClick={addAttendee}>Add</Button>
        </div>
        {form.attendees.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {form.attendees.map(email => (
              <Badge key={email} variant="secondary" className="cursor-pointer" onClick={() => removeAttendee(email)}>
                {email} ×
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="save-event-btn">{saving ? 'Saving...' : 'Save Event'}</Button>
      </div>
    </form>
  );
};

// Calendar Day Cell
const CalendarDay = ({ date, events, isCurrentMonth, isToday, onDateClick }) => {
  const dayEvents = events.filter(e => {
    const eventDate = new Date(e.start_datetime).toDateString();
    return eventDate === date.toDateString();
  });

  return (
    <div
      className={`min-h-[100px] p-2 border-r border-b cursor-pointer hover:bg-muted/50 transition-colors ${!isCurrentMonth ? 'bg-muted/30 text-muted-foreground' : ''}`}
      onClick={() => onDateClick(date)}
    >
      <div className={`text-sm font-medium mb-1 ${isToday ? 'h-7 w-7 rounded-full bg-rose-600 text-white flex items-center justify-center' : ''}`}>
        {date.getDate()}
      </div>
      <div className="space-y-1">
        {dayEvents.slice(0, 3).map(event => (
          <div
            key={event.id}
            className={`text-xs px-1.5 py-0.5 rounded truncate text-white ${eventTypeColors[event.event_type]}`}
            title={event.title}
          >
            {event.title}
          </div>
        ))}
        {dayEvents.length > 3 && (
          <div className="text-xs text-muted-foreground">+{dayEvents.length - 3} more</div>
        )}
      </div>
    </div>
  );
};

// Main Calendar Page
export const CalendarPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    loadEvents();
  }, [currentDate]);

  const loadEvents = async () => {
    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const res = await axios.get(`${API}/calendar/events`, {
        params: {
          start_date: startOfMonth.toISOString(),
          end_date: endOfMonth.toISOString()
        }
      });
      setEvents(res.data);
    } catch (error) {
      console.error('Failed to load events:', error);
    } finally {
      setLoading(false);
    }
  };

  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    for (let i = startingDay - 1; i >= 0; i--) {
      const date = new Date(year, month, -i);
      days.push({ date, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      days.push({ date, isCurrentMonth: true });
    }
    
    // Next month days to fill the grid
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ date, isCurrentMonth: false });
    }
    
    return days;
  }, [currentDate]);

  const today = new Date();
  const isToday = (date) => date.toDateString() === today.toDateString();

  const navigateMonth = (delta) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + delta, 1));
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    setSelectedEvent(null);
    setEventDialogOpen(true);
  };

  const upcomingEvents = events
    .filter(e => new Date(e.start_datetime) >= new Date())
    .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))
    .slice(0, 5);

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Calendar</h1>
          <p className="text-muted-foreground mt-1">Schedule meetings, deadlines, and reminders</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="px-3 py-1">
            <Link className="h-4 w-4 mr-2" />
            Ready for Outlook Sync
          </Badge>
          <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setSelectedEvent(null); setSelectedDate(null); }} data-testid="add-event-btn">
                <Plus className="h-4 w-4 mr-2" /> New Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>{selectedEvent ? 'Edit Event' : 'New Event'}</DialogTitle>
              </DialogHeader>
              <EventForm 
                event={selectedEvent || (selectedDate ? { start_datetime: selectedDate.toISOString().slice(0, 16), end_datetime: new Date(selectedDate.getTime() + 3600000).toISOString().slice(0, 16) } : null)} 
                onSave={loadEvents} 
                onClose={() => setEventDialogOpen(false)} 
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendar Grid */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">
                  {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>
                    Today
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Days Header */}
              <div className="grid grid-cols-7 border-b">
                {DAYS.map(day => (
                  <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground border-r">
                    {day}
                  </div>
                ))}
              </div>
              {/* Calendar Grid */}
              <div className="grid grid-cols-7">
                {calendarDays.map(({ date, isCurrentMonth }, idx) => (
                  <CalendarDay
                    key={idx}
                    date={date}
                    events={events}
                    isCurrentMonth={isCurrentMonth}
                    isToday={isToday(date)}
                    onDateClick={handleDateClick}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Upcoming Events */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Upcoming Events</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {upcomingEvents.length === 0 ? (
                <p className="text-sm text-muted-foreground">No upcoming events</p>
              ) : (
                upcomingEvents.map(event => (
                  <div
                    key={event.id}
                    className="p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => { setSelectedEvent(event); setEventDialogOpen(true); }}
                  >
                    <div className="flex items-start gap-2">
                      <div className={`h-2 w-2 rounded-full mt-1.5 ${eventTypeColors[event.event_type]}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{event.title}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {new Date(event.start_datetime).toLocaleDateString()} {new Date(event.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        {event.location && (
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <MapPin className="h-3 w-3" />
                            {event.location}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Event Type Legend */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Event Types</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {EVENT_TYPES.map(type => (
                <div key={type} className="flex items-center gap-2">
                  <div className={`h-3 w-3 rounded-full ${eventTypeColors[type]}`} />
                  <span className="text-sm capitalize">{type}</span>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Outlook Sync Info */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <CalendarIcon className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">Outlook Integration</p>
                  <p className="text-sm text-blue-700 mt-1">
                    Calendar is ready for Microsoft Outlook sync. Connect your account in Settings.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
